/**
 * StrategyHub.jsx - Post-lock Strategy Analysis
 * Phase 3B Round 3 - Named export only
 * 
 * Extracted from gm-strategy-hub-v22.jsx lines 891-979
 */

import React from 'react';
import { Users, BarChart2, ChevronRight, Swords } from 'lucide-react';

// UI components - 2 levels up from features/strategy/
import { LoadingSpinner, ErrorState } from '../../components/ui';

// Hooks - 2 levels up
import { useFieldOverview } from '../../hooks/useGMStrategy';

export const StrategyHub = ({ pool, onPortfolio, onBattleWhatIf }) => {
  const { fieldData, loading, error, refresh } = useFieldOverview(pool?.id);
  
  return (
    <div className="min-h-screen bg-slate-100 pb-20">
      <header className="bg-white shadow-sm px-4 py-3">
        <h1 className="text-base font-bold text-slate-900 text-center">Game Plan</h1>
      </header>
      <main className="p-3 space-y-3">
        <p className="text-[11px] text-slate-500 px-1">Post-lock analysis and competitive modeling.</p>
        
        {/* Field Overview Section */}
        {loading ? (
          <LoadingSpinner message="Loading field data..." />
        ) : error ? (
          <ErrorState message={`Failed to load field data: ${error}`} onRetry={refresh} />
        ) : fieldData ? (
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Users size={16} className="text-indigo-500" />
              Field Overview
            </h3>
            
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-2">
                <p className="text-lg font-black text-slate-800">{fieldData.totalEntrants}</p>
                <p className="text-[9px] text-slate-500">Entrants</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <p className="text-lg font-black text-slate-800">{fieldData.lockedEntrants}</p>
                <p className="text-[9px] text-slate-500">Locked</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <p className="text-lg font-black text-slate-800">{fieldData.completionPct}%</p>
                <p className="text-[9px] text-slate-500">Complete</p>
              </div>
            </div>
            
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Popular Players</p>
              {fieldData.popularPlayers?.length > 0 ? (
                <div className="space-y-1">
                  {fieldData.popularPlayers.slice(0, 3).map((p, i) => (
                    <div key={p.playerId} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">{p.name}</span>
                      <span className="text-indigo-600 font-semibold">{p.ownership}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No data yet</p>
              )}
            </div>
            
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Avg Stats</p>
              <div className="flex gap-4 text-xs">
                <span className="text-slate-600">Units: <strong>{fieldData.avgUnitsUsed}</strong></span>
                <span className="text-slate-600">Slots: <strong>{fieldData.avgFilledSlots}</strong></span>
              </div>
            </div>
          </div>
        ) : null}
        
        <button onClick={onPortfolio} className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 active:scale-[0.98]">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <BarChart2 size={24} className="text-indigo-600" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="font-bold text-slate-800">Roster Review</h3>
            <p className="text-[11px] text-slate-500">See your locked picks</p>
          </div>
          <ChevronRight size={18} className="text-slate-400" />
        </button>
        
        <button onClick={onBattleWhatIf} className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 active:scale-[0.98]">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <Swords size={24} className="text-amber-600" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="font-bold text-slate-800">Head-to-Head</h3>
            <p className="text-[11px] text-slate-500">See where your picks differ</p>
          </div>
          <ChevronRight size={18} className="text-slate-400" />
        </button>
      </main>
    </div>
  );
};
